import { useState } from "react";
import { toast } from "sonner";
import { api, errMsg, useApi } from "../../lib/api";
import { useAuth, hasRole } from "../../context/AuthContext";
import { SampleBadge, Decision } from "../Badges";

const COLS = [["todo", "Assigned"], ["accepted", "Accepted"], ["in_progress", "In progress"], ["done", "Done"]];
const PRI = { high: "pill-red", medium: "pill-amber", low: "pill-slate" };

const NewTask = ({ staff, onDone }) => {
  const [f, setF] = useState({ title: "", assignee_id: "", priority: "medium", station: "Peace FM", platform: "radio", due: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/tasks", { ...f, due: f.due || null });
      toast.success("Task delegated");
      setF({ ...f, title: "" });
      onDone();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <form onSubmit={submit} className="panel p-4 grid md:grid-cols-6 gap-3 mb-6" data-testid="new-task-form">
      <input required className="field md:col-span-2" placeholder="What needs doing?" value={f.title} onChange={set("title")} data-testid="task-title-input" />
      <select required className="field" value={f.assignee_id} onChange={set("assignee_id")} data-testid="task-assignee-select">
        <option value="">Assign to…</option>
        {(staff || []).map((s) => (
          <option key={s.id} value={s.id} disabled={s.status === "on_leave"}>{s.name} — {s.status === "on_leave" ? "on leave" : `${s.effective_status.replace("_", " ")}, ${s.open_tasks} open`}</option>
        ))}
      </select>
      <select className="field" value={f.priority} onChange={set("priority")} data-testid="task-priority-select">{["high", "medium", "low"].map((p) => <option key={p}>{p}</option>)}</select>
      <select className="field" value={f.platform} onChange={set("platform")} data-testid="task-platform-select">{["radio", "tv", "digital"].map((p) => <option key={p}>{p}</option>)}</select>
      <button className="btn-gold" data-testid="task-create-btn">Delegate</button>
    </form>
  );
};

export const Tasks = () => {
  const { user } = useAuth();
  const { data, reload } = useApi("/tasks");
  const { data: staff, reload: reloadStaff } = useApi("/staff");
  const mgr = hasRole(user, ["manager", "hr", "gm", "executive"]);
  const move = async (id, status) => {
    try {
      await api.patch(`/tasks/${id}`, { status });
      reload();
      reloadStaff();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const next = { todo: [["accepted", "Accept"], ["rejected", "Reject"]], accepted: [["in_progress", "Start"]], in_progress: [["done", "Complete"]] };
  return (
    <div data-testid="tasks-board">
      {mgr && <NewTask staff={staff} onDone={() => { reload(); reloadStaff(); }} />}
      {!mgr && <p className="text-sm text-slate-400 mb-4">Tasks assigned to you. Accept, start and complete them here.</p>}
      <div className="grid md:grid-cols-4 gap-4">
        {COLS.map(([k, l]) => (
          <div key={k} className="bg-[#F4F8F5] border border-[color:var(--line)] rounded-sm p-3 min-h-[200px]" data-testid={`task-col-${k}`}>
            <div className="eyebrow !text-[10px] mb-3">{l} · {(data || []).filter((t) => t.status === k).length}</div>
            <div className="space-y-2">
              {(data || []).filter((t) => t.status === k).map((t) => (
                <div key={t.id} className="panel p-3" data-testid={`task-card-${t.id}`}>
                  <div className="text-sm text-ink">{t.title}</div>
                  <div className="text-[11px] text-slate-400 mt-1">{t.assignee_name} · {t.station} · {t.platform}</div>
                  <div className="flex flex-wrap gap-1 mt-2"><span className={`pill ${PRI[t.priority]}`}>{t.priority}</span>{t.is_sample && <SampleBadge label="Sample" />}</div>
                  <div className="flex gap-2 mt-2">
                    {(next[t.status] || []).map(([s, lbl]) => (
                      <button key={s} onClick={() => move(t.id, s)} className="text-[11px] text-gold hover:underline" data-testid={`task-${s}-btn-${t.id}`}>{lbl}</button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <Decision>Delegate to people who are actually free — on-leave staff cannot be assigned and current load shows beside every name.</Decision>
    </div>
  );
};
